import React, { useState, useCallback } from 'react';
import { Box, Typography, Paper, IconButton } from '@mui/material';
import { CloudUpload as CloudUploadIcon, Close as CloseIcon } from '@mui/icons-material';
import useI18n from '../../../hooks/useI18n';

function ImageUploadBox({ onImageSelect, initialPreview = null, accept = 'image/*' }) {
    const { l } = useI18n();
    const [isDragging, setIsDragging] = useState(false);
    const [preview, setPreview] = useState(initialPreview);
    const fileInputRef = React.useRef(null);

    const handleFile = useCallback((file) => {
        if (file && file.type.startsWith('image/')) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setPreview(reader.result);
            };
            reader.readAsDataURL(file);
            onImageSelect(file);
        }
    }, [onImageSelect]);

    const handleDragEnter = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    }, []);

    const handleDragLeave = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    }, []);

    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
    }, []);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
            handleFile(files[0]);
        }
    }, [handleFile]);

    const handleFileSelect = useCallback((e) => {
        const files = e.target.files;
        if (files && files.length > 0) {
            handleFile(files[0]);
        }
    }, [handleFile]);

    const handleClick = useCallback(() => {
        fileInputRef.current?.click();
    }, []);

    const handleRemove = useCallback((e) => {
        e.stopPropagation();
        setPreview(null);
        onImageSelect(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    }, [onImageSelect]);

    React.useEffect(() => {
        setPreview(initialPreview);
    }, [initialPreview]);

    return (
        <Box sx={{ width: '100%' }}>
            <input
                ref={fileInputRef}
                type="file"
                accept={accept}
                style={{ display: 'none' }}
                onChange={handleFileSelect}
            />
            
            <Paper
                elevation={isDragging ? 8 : 1}
                sx={{
                    position: 'relative',
                    border: isDragging ? '2px dashed #1976d2' : '2px dashed #ccc',
                    borderRadius: 2,
                    padding: 3,
                    textAlign: 'center',
                    cursor: 'pointer',
                    backgroundColor: isDragging ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                    transition: 'all 0.3s ease',
                    minHeight: 200,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '&:hover': {
                        borderColor: '#1976d2',
                        backgroundColor: 'rgba(25, 118, 210, 0.04)',
                    },
                }}
                onDragEnter={handleDragEnter}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={handleClick}
            >
                {preview ? (
                    <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
                        <IconButton
                            sx={{
                                position: 'absolute',
                                top: -10,
                                right: -10,
                                backgroundColor: 'white',
                                boxShadow: 2,
                                '&:hover': {
                                    backgroundColor: '#f5f5f5',
                                },
                            }}
                            onClick={handleRemove}
                            size="small"
                        >
                            <CloseIcon />
                        </IconButton>
                        <Box
                            component="img"
                            src={preview}
                            alt="Preview"
                            sx={{
                                maxWidth: '100%',
                                maxHeight: 300,
                                objectFit: 'contain',
                                borderRadius: 1,
                            }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                            {l('Click to change image')}
                        </Typography>
                    </Box>
                ) : (
                    <>
                        <CloudUploadIcon sx={{ fontSize: 60, color: 'action.active', mb: 2 }} />
                        <Typography variant="h6" gutterBottom>
                            {l('Drag and drop image here')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {l('or click to select from folder')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                            {l('Supported formats')}: JPG, PNG, GIF, WEBP
                        </Typography>
                    </>
                )}
            </Paper>
        </Box>
    );
}

export default ImageUploadBox;
